import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import axios from 'axios';
import {
  Users, BookOpen, GraduationCap, Crown, Shield, User, Layers
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MyClassPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/my-class`);
        setData(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="flex justify-center p-12"><div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;

  if (!data?.assigned) {
    return (
      <div className="p-4 sm:p-6" data-testid="my-class-page">
        <h1 className="text-xl font-bold flex items-center gap-2 mb-4"><Layers className="w-5 h-5" /> Ma Classe</h1>
        <Card>
          <CardContent className="py-16 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-200" />
            <p className="text-gray-500 text-lg font-medium">
              {language === 'fr' ? "Vous n'etes pas encore affecte a une classe" : "You are not assigned to a class yet"}
            </p>
            <p className="text-gray-400 text-sm mt-2">
              {language === 'fr' ? "Contactez votre administration pour etre affecte a un groupe" : "Contact your administration to be assigned to a group"}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const cg = data.class_group;
  const { students, courses, stats } = data;

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="my-class-page">
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2"><Layers className="w-5 h-5" /> Ma Classe</h1>
        <p className="text-sm text-gray-500">{cg.program?.name} - {cg.promotion?.year_label} ({cg.promotion?.academic_year})</p>
      </div>

      {/* Header Card */}
      <Card className="border-l-4 border-l-blue-500">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold" data-testid="my-class-name">{cg.name}</h2>
              <div className="flex gap-2 mt-1 flex-wrap">
                {cg.leader_name && (
                  <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                    <Crown className="w-3 h-3 mr-1" />Chef: {cg.leader_name}
                  </Badge>
                )}
                {cg.deputy_name && (
                  <Badge className="bg-blue-100 text-blue-700 text-xs">
                    <Shield className="w-3 h-3 mr-1" />Adjoint: {cg.deputy_name}
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600" data-testid="students-count">{stats.total_students}</p>
                <p className="text-[10px] text-gray-500">Etudiants</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600" data-testid="courses-count">{stats.total_courses}</p>
                <p className="text-[10px] text-gray-500">Cours</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="classmates">
        <TabsList>
          <TabsTrigger value="classmates" data-testid="tab-classmates"><Users className="w-4 h-4 mr-1" />Camarades ({stats.total_students})</TabsTrigger>
          <TabsTrigger value="courses" data-testid="tab-courses"><BookOpen className="w-4 h-4 mr-1" />Cours ({stats.total_courses})</TabsTrigger>
        </TabsList>

        <TabsContent value="classmates">
          <ScrollArea className="h-[400px]">
            <div className="space-y-1">
              {students.map(s => {
                const isMe = s.user_id === user?.id;
                return (
                  <Card key={s.user_id} className={isMe ? 'ring-1 ring-blue-300 bg-blue-50/30' : ''}>
                    <CardContent className="p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium ${isMe ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                          {s.first_name?.[0]}{s.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            {s.first_name} {s.last_name}
                            {isMe && <span className="text-blue-500 ml-1">(moi)</span>}
                          </p>
                          <p className="text-[10px] text-gray-400">{s.matricule}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {s.is_leader && <Badge className="bg-yellow-100 text-yellow-700 text-[10px]"><Crown className="w-3 h-3 mr-0.5" />Chef</Badge>}
                        {s.is_deputy && <Badge className="bg-blue-100 text-blue-700 text-[10px]"><Shield className="w-3 h-3 mr-0.5" />Adjoint</Badge>}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="courses">
          {courses.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-gray-400 text-sm">Aucun cours assigne a cette classe.</CardContent></Card>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="space-y-1">
                {courses.map(c => (
                  <Card key={c.id}>
                    <CardContent className="p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <BookOpen className="w-5 h-5 text-green-500" />
                        <div>
                          <p className="font-medium text-sm">{c.name}</p>
                          <p className="text-[10px] text-gray-400">{c.code} - {c.credits} credits</p>
                        </div>
                      </div>
                      {c.teacher_name && (
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <User className="w-3 h-3" /> {c.teacher_name}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
